import React, { useCallback } from 'react';
import { Paper, Button, makeStyles, Icon, Theme, IconButton, FormHelperText } from '@material-ui/core';
import clsx from 'clsx';
import { usePropValue, inputStateSetterHelper, useModePanelStyle, useModeFormStyle, FontIcon, BaseInfoCompProps, BaseEntityField,
    BaseInfoCompSaveAction, BaseInfoCompActionsSet, InfoCompPanelHeader, ModeInfoCompTextInputField } from '@moderepo/mode-app-components';


const useStyle = makeStyles((theme: Theme) => {
    return {
        form: {
            justifyContent: 'flex-start',
        },

        channelsTitle: {
            fontWeight: 500,
            fontSize  : 'larger',
            margin    : theme.spacing(2, 0, 2, 0),
        },

        channelContainer: {
            border       : `1px solid ${theme.palette.divider}`,
            borderRadius : 4,
            marginBottom : theme.spacing(2),
            position     : 'relative',
            width        : '100%',
            display      : 'flex',
            alignItems   : 'center',
            flexDirection: 'row',

            '& .legend': {
                padding   : theme.spacing(0, 1),
                fontWeight: 'bold',
                fontSize  : 'larger',
            },
            '& .remove-button': {
                color    : theme.palette.secondary.main,
                position : 'absolute',
                top      : 0,
                right    : 0,
                transform: 'translate3d(50%, -50%, 0)',
                zIndex   : 2,
            },
        },

        channelsActions: {
            margin        : theme.spacing(1),
            display       : 'flex',
            alignItems    : 'center',
            width         : '100%',
            justifyContent: 'center',
        },
    };
}, { name: 'RCSlackSettingsComp', index: 1 });


interface SlackChannelInput {
    readonly channelId?: string | undefined;
    readonly name?: string | undefined;
}


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface RCSlackSettingsCompUpdatableInputs {
    readonly botUserAccessToken?: string | undefined;
    readonly channels?: readonly SlackChannelInput[] | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCSlackSettingsCompActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<RCSlackSettingsCompUpdatableInputs>
}


export interface RCSlackSettingsCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly botUserAccessToken: BaseEntityField<string>;
        readonly channels: BaseEntityField<readonly SlackChannelInput[]>;
    }
    readonly actions?: RCSlackSettingsCompActionsSet | undefined;
    readonly texts: {
        readonly channelNameLabel: string;
        readonly channelIdLabel: string;
        readonly addChannelButtonLabel: string;
    }
}


/**
 * This is a DUMB component used for displaying robot cloud project slack settings. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCSlackSettingsComp: React.FC<RCSlackSettingsCompProps> = (props: RCSlackSettingsCompProps) => {
    const [botUserAccessTokenInput, setBotUserAccessTokenInput] = usePropValue<string | undefined>(props.fields.botUserAccessToken.value);
    const [channelsInput, setChannelsInput] = usePropValue<readonly SlackChannelInput[] | undefined>(props.fields.channels.value);
    const classes = useStyle();

    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            botUserAccessToken: setBotUserAccessTokenInput,
        });
    }, [setBotUserAccessTokenInput]);



    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setBotUserAccessTokenInput(props.fields.botUserAccessToken.value);
        setChannelsInput(props.fields.channels.value);
    }, [props.fields.botUserAccessToken.value, props.fields.channels.value, setBotUserAccessTokenInput, setChannelsInput]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                botUserAccessToken: botUserAccessTokenInput?.trim(),
                channels          : channelsInput,
            });
        }

        return false;
    }, [props.actions, botUserAccessTokenInput, channelsInput]);


    const addChannel = useCallback(() => {
        setChannelsInput([
            ...(channelsInput || []),
            {
                channelId: undefined,
                name     : undefined,
            },
        ]);
        if (props.onFieldClicked) {
            props.onFieldClicked('channels');
        }
    }, [channelsInput, props, setChannelsInput]);



    /**
     * Remove a channel at the specified index
     */
    const removeChannel = useCallback((channelIndex: number) => {
        return setChannelsInput(channelsInput?.filter((channel, index) => {
            return index !== channelIndex;
        }));
    }, [channelsInput, setChannelsInput]);


    /**
     * Handler for input change event when the user change the channelId or name.
     */
    const onOneChannelInputChanged = useCallback((channelIndex: number, fieldName: string, value: string) => {
        // Update the channel at channelIndex with the given field's name/value and then update the whole channelInputs
        // with a new array. All the other channels in the array will be the same except for the channel that was modified.
        setChannelsInput(channelsInput?.map((channel, index) => {
            if (index === channelIndex) {
                // Update the channel that matched the channelIndex. Replace the specified field (channelId or name) with the new value
                return {
                    ...channel,
                    [fieldName]: value,
                };
            }
            return channel;
        }));
    }, [channelsInput, setChannelsInput]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className, classes.form)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={clsx(formClasses.formInputGroup)}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            multiline
                            compProps={props}
                            inputProps={props.fields.botUserAccessToken}
                            fieldName="botUserAccessToken"
                            inputValue={props.fields.botUserAccessToken.editable
                                ? botUserAccessTokenInput
                                : props.fields.botUserAccessToken.displayValue ?? props.fields.botUserAccessToken.value}
                            onInputChange={onInputChange}
                        />

                    </div>
                    {channelsInput && channelsInput.length > 0 && (
                        <>
                            <div className={classes.channelsTitle}>{props.fields.channels.label}</div>
                            <div className={clsx(formClasses.formInputGroup, 'medium')}>
                                {channelsInput.map((channel, index) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <fieldset className={classes.channelContainer} key={`${index}`}>
                                            {props.isEditing && (
                                                <IconButton
                                                    className="remove-button"
                                                    onClick={() => {
                                                        removeChannel(index);
                                                    }}
                                                ><Icon>cancel</Icon>
                                                </IconButton>
                                            )}
                                            <div className={clsx(formClasses.formInputGroup, 'x-small')}>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    compProps={props}
                                                    inputProps={{
                                                        ...props.fields.channels,
                                                        label: props.texts.channelIdLabel,
                                                        error: '',
                                                    }}
                                                    fieldName="channel.channelId"
                                                    inputValue={channel.channelId}
                                                    onInputChange={(fieldName, value) => {
                                                        onOneChannelInputChanged(index, 'channelId', value ? value.toString() : '');
                                                    }}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    compProps={props}
                                                    inputProps={{
                                                        ...props.fields.channels,
                                                        label: props.texts.channelNameLabel,
                                                        error: '',
                                                    }}
                                                    fieldName="channel.name"
                                                    inputValue={channel.name}
                                                    onInputChange={(fieldName, value) => {
                                                        onOneChannelInputChanged(index, 'name', value ? value.toString() : '');
                                                    }}
                                                />
                                            </div>
                                        </fieldset>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    {props.fields.channels.error && (
                        <FormHelperText error>
                            {props.fields.channels.error}
                        </FormHelperText>
                    )}
                    <div className={clsx(classes.channelsActions)}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Icon>add</Icon>}
                            onClick={addChannel}
                        >
                            {props.texts.addChannelButtonLabel}
                        </Button>
                    </div>

                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
